import React, { useEffect, useRef, useState } from 'react';
import { createChart } from 'lightweight-charts';

const BalanceChart = ({ data }) => {
  const chartContainerRef = useRef();
  const chartRef = useRef();
  const tonSeriesRef = useRef();
  const buytSeriesRef = useRef();
  const usdSeriesRef = useRef();
  
  // State for toggling series visibility
  const [showTon, setShowTon] = useState(true);
  const [showBuyt, setShowBuyt] = useState(true);
  const [showUsd, setShowUsd] = useState(true);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Create chart
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 400,
      layout: {
        background: { color: '#ffffff' },
        textColor: '#333',
      },
      grid: {
        vertLines: { color: '#f0f0f0' },
        horzLines: { color: '#f0f0f0' },
      },
      rightPriceScale: {
        borderColor: '#d1d4dc',
      },
      timeScale: {
        borderColor: '#d1d4dc',
        timeVisible: true,
        secondsVisible: false,
      },
    });

    chartRef.current = chart;

    // Create three series
    const tonSeries = chart.addLineSeries({
      color: '#0088cc',
      lineWidth: 2,
      title: 'TON Balance',
    });
    tonSeriesRef.current = tonSeries;

    const buytSeries = chart.addLineSeries({
      color: '#ff6b35',
      lineWidth: 2,
      title: 'BUYT Balance',
      priceScaleId: 'left',
    });
    buytSeriesRef.current = buytSeries;

    const usdSeries = chart.addLineSeries({
      color: '#00c851',
      lineWidth: 2,
      title: 'USD Value',
    });
    usdSeriesRef.current = usdSeries;

    // Add left price scale for BUYT
    chart.priceScale('left').applyOptions({
      borderColor: '#d1d4dc',
    });

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (!data || data.length === 0) return;
    if (!tonSeriesRef.current || !buytSeriesRef.current || !usdSeriesRef.current) return;

    // Transform data to chart format
    const tonData = [];
    const buytData = [];
    const usdData = [];

    // Use a Set to track unique timestamps and avoid duplicates
    const seenTimestamps = new Set();

    data.forEach((snapshot) => {
      if (!snapshot || !snapshot.timestamp) return;
      
      let timestamp = Math.floor(new Date(snapshot.timestamp).getTime() / 1000);
      
      // If we've seen this timestamp, add 1 second to make it unique
      while (seenTimestamps.has(timestamp)) {
        timestamp += 1;
      }
      seenTimestamps.add(timestamp);
      
      // Only add data if values are valid numbers
      if (snapshot.total_ton != null && !isNaN(snapshot.total_ton) && isFinite(snapshot.total_ton)) {
        tonData.push({
          time: timestamp,
          value: Number(snapshot.total_ton),
        });
      }
      
      // BUYT balance - ensure it's a valid number (allow 0 values)
      const buytValue = snapshot.total_buyt;
      if (buytValue != null && !isNaN(buytValue) && isFinite(buytValue) && buytValue >= 0) {
        buytData.push({
          time: timestamp,
          value: Number(buytValue),
        });
      }
      
      if (snapshot.total_usd != null && !isNaN(snapshot.total_usd) && isFinite(snapshot.total_usd)) {
        usdData.push({
          time: timestamp,
          value: Number(snapshot.total_usd),
        });
      }
    });

    // Sort data by time (ascending order) to ensure proper ordering
    const sortByTime = (a, b) => a.time - b.time;
    tonData.sort(sortByTime);
    buytData.sort(sortByTime);
    usdData.sort(sortByTime);

    // Log data for debugging
    console.log('BalanceChart data:', {
      totalSnapshots: data.length,
      tonDataPoints: tonData.length,
      buytDataPoints: buytData.length,
      usdDataPoints: usdData.length,
      sampleBuyt: buytData.length > 0 ? buytData[0] : null,
      lastBuyt: buytData.length > 0 ? buytData[buytData.length - 1] : null,
      sampleRawData: data.length > 0 ? {
        timestamp: data[0].timestamp,
        total_ton: data[0].total_ton,
        total_buyt: data[0].total_buyt,
        total_usd: data[0].total_usd
      } : null
    });

    // Set data to series
    tonSeriesRef.current.setData(tonData);
    buytSeriesRef.current.setData(buytData);
    usdSeriesRef.current.setData(usdData);
    
    // Update visibility
    tonSeriesRef.current.applyOptions({ visible: showTon });
    buytSeriesRef.current.applyOptions({ visible: showBuyt });
    usdSeriesRef.current.applyOptions({ visible: showUsd });

    // Fit content
    if (chartRef.current) {
      chartRef.current.timeScale().fitContent();
    }
  }, [data]);

  // Update visibility when toggles change
  useEffect(() => {
    if (tonSeriesRef.current) {
      tonSeriesRef.current.applyOptions({ visible: showTon });
    }
    if (buytSeriesRef.current) {
      buytSeriesRef.current.applyOptions({ visible: showBuyt });
    }
    if (usdSeriesRef.current) {
      usdSeriesRef.current.applyOptions({ visible: showUsd });
    }
  }, [showTon, showBuyt, showUsd]);

  return (
    <div>
      <div ref={chartContainerRef} />
      <div className="flex flex-wrap justify-center gap-4 mt-3 text-sm">
        <label className="flex items-center gap-2 cursor-pointer hover:opacity-80">
          <input
            type="checkbox"
            checked={showTon}
            onChange={(e) => setShowTon(e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <div className="w-4 h-0.5 bg-[#0088cc]"></div>
          <span>TON Balance</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer hover:opacity-80">
          <input
            type="checkbox"
            checked={showBuyt}
            onChange={(e) => setShowBuyt(e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <div className="w-4 h-0.5 bg-[#ff6b35]"></div>
          <span>BUYT Balance</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer hover:opacity-80">
          <input
            type="checkbox"
            checked={showUsd}
            onChange={(e) => setShowUsd(e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <div className="w-4 h-0.5 bg-[#00c851]"></div>
          <span>USD Value</span>
        </label>
      </div>
    </div>
  );
};

export default BalanceChart;
